import {
  ChevronLeft,
  Link as LinkIcon,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  Settings,
  SmilePlus,
  UserPlus,
  Users,
  VideoOff,
} from "lucide-react";
/**
 * HERO app-window module — "Online-Meetings" (chromeless, full-bleed).
 *
 * 1:1 port of the prototype `isMeeting` blocks (content + floating phone
 * overlay). Dark video-grid that owns the entire window interior; a floating
 * iPhone overlay hangs below the window at `bottom:-44px`.
 *
 * Source: `Hausverwaltung Hero.dc.html` lines 1776–1816 (content) and
 * 1887–1914 (floating phone overlay).
 */
import type { ReactNode } from "react";

import {
  MEET_DANIEL_ALT,
  MEET_DANIEL_TITLE,
  MEET_WOLFGANG_ALT,
  MEET_WOLFGANG_TITLE,
} from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Whitespace-flush mono-tabular numerals match the prototype timer chips. */
const TABULAR = "tabular-nums" as const;

/** A control-dock circular icon button (40×40). */
function DockButton(props: {
  readonly children: ReactNode;
  readonly background?: string;
}): ReactNode {
  return (
    <span
      style={{
        width: 40,
        height: 40,
        minWidth: 40,
        minHeight: 40,
        flex: "none",
        flexShrink: 0,
        borderRadius: "50%",
        background: props.background ?? "rgba(255,255,255,0.1)",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {props.children}
    </span>
  );
}

/** The four-tile video grid: two photo tiles + two avatar tiles. */
function VideoGrid(): ReactNode {
  const t = useTranslations("marketing.moduleMeeting");
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        padding: 16,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: 14,
      }}
    >
      {/* Daniel Weiß — photo */}
      <div
        style={{
          position: "relative",
          borderRadius: 14,
          overflow: "hidden",
          background: "#1a1c1f",
        }}
      >
        <img
          src="/marketing/meet/meet-1.jpg"
          alt={MEET_DANIEL_ALT}
          title={MEET_DANIEL_TITLE}
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 30%",
            display: "block",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 11,
            bottom: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(0,0,0,0.46)",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            padding: "4px 10px",
            borderRadius: 8,
          }}
        >
          <Mic
            width={13}
            height={13}
            strokeWidth={2}
            style={{ color: "#fff" }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              fontFamily: H.sans,
            }}
          >
            {t("danielWeiss")}
          </span>
        </div>
      </div>

      {/* Sandra Holtkamp — avatar tile (muted) */}
      <div
        style={{
          position: "relative",
          borderRadius: 14,
          overflow: "hidden",
          background: "#202327",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "#39414B",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 600,
            fontFamily: H.sans,
          }}
        >
          {t("sh")}
        </span>
        <div
          style={{
            position: "absolute",
            left: 11,
            bottom: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(0,0,0,0.4)",
            padding: "4px 10px",
            borderRadius: 8,
          }}
        >
          <MicOff
            width={13}
            height={13}
            strokeWidth={2}
            style={{ color: "#F26D6D" }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              fontFamily: H.sans,
            }}
          >
            {t("sandraHoltkamp")}
          </span>
        </div>
      </div>

      {/* Wolfgang Reuter — photo with accent ring */}
      <div
        style={{
          position: "relative",
          borderRadius: 14,
          overflow: "hidden",
          background: "#1a1c1f",
          boxShadow: `inset 0 0 0 2.5px ${H.accent}`,
        }}
      >
        <img
          src="/marketing/meet/meet-mobile.jpg"
          alt={MEET_WOLFGANG_ALT}
          title={MEET_WOLFGANG_TITLE}
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            transform: "translateY(-14%)",
            display: "block",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 11,
            bottom: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            background: "rgba(0,0,0,0.46)",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            padding: "4px 10px",
            borderRadius: 8,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "flex-end",
              gap: 1.5,
              height: 11,
            }}
          >
            <span
              style={{
                width: 2,
                height: 5,
                background: H.accent,
                borderRadius: 1,
              }}
            />
            <span
              style={{
                width: 2,
                height: 9,
                background: H.accent,
                borderRadius: 1,
              }}
            />
            <span
              style={{
                width: 2,
                height: 7,
                background: H.accent,
                borderRadius: 1,
              }}
            />
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              fontFamily: H.sans,
            }}
          >
            {t("wolfgangReuter")}
          </span>
        </div>
      </div>

      {/* Jens Bauer — avatar tile */}
      <div
        style={{
          position: "relative",
          borderRadius: 14,
          overflow: "hidden",
          background: "#202327",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "#39414B",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 600,
            fontFamily: H.sans,
          }}
        >
          {t("jb")}
        </span>
        <div
          style={{
            position: "absolute",
            left: 11,
            bottom: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(0,0,0,0.4)",
            padding: "4px 10px",
            borderRadius: 8,
          }}
        >
          <Mic
            width={13}
            height={13}
            strokeWidth={2}
            style={{ color: "#fff" }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              fontFamily: H.sans,
            }}
          >
            {t("jensBauer")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Bottom floating control dock. */
function ControlDock(): ReactNode {
  const t = useTranslations("marketing.moduleMeeting");
  return (
    <div
      className="mkhero-meeting-dock-shell"
      style={{
        position: "absolute",
        left: "50%",
        bottom: 18,
        transform: "translateX(-50%)",
        maxWidth: "calc(100% - 24px)",
      }}
    >
      <div
        className="mkhero-meeting-dock"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 9,
          width: "max-content",
          flexWrap: "nowrap",
          background: "rgba(22,24,27,0.9)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: 18,
          padding: "9px 12px",
          boxShadow: "0 18px 44px -16px rgba(0,0,0,0.6)",
        }}
      >
        <DockButton>
          <Mic width={18} height={18} strokeWidth={2} />
        </DockButton>
        <DockButton background="#D8453E">
          <VideoOff width={18} height={18} strokeWidth={2} />
        </DockButton>
        <DockButton>
          <MonitorUp width={18} height={18} strokeWidth={2} />
        </DockButton>
        <DockButton>
          <SmilePlus width={18} height={18} strokeWidth={2} />
        </DockButton>
        <DockButton>
          <Settings width={18} height={18} strokeWidth={2} />
        </DockButton>
        <span
          style={{
            width: 1,
            height: 26,
            flex: "none",
            flexShrink: 0,
            background: "rgba(255,255,255,0.14)",
            margin: "0 2px",
          }}
        />
        <DockButton>
          <MessageSquare width={18} height={18} strokeWidth={2} />
        </DockButton>
        <DockButton background={H.accent}>
          <Users width={18} height={18} strokeWidth={2} />
        </DockButton>
        <span
          style={{
            height: 40,
            flex: "none",
            flexShrink: 0,
            padding: "0 17px",
            borderRadius: 21,
            background: "#D8453E",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13.5,
            fontWeight: 600,
            fontFamily: H.sans,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <PhoneOff width={16} height={16} strokeWidth={2} />
          {t("verlassenBeenden")}
        </span>
      </div>
    </div>
  );
}

/**
 * Floating iPhone overlay (Sandra Holtkamp), hanging below the window. A
 * frame-level overlay rendered via the registry `Overlay` slot; it positions
 * itself absolutely relative to the aurora frame so it overflows the window.
 */
export function MeetingPhoneOverlay(): ReactNode {
  const t = useTranslations("marketing.moduleMeeting");
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        bottom: -44,
        zIndex: 30,
        width: 244,
      }}
    >
      <div
        style={{
          position: "relative",
          background: "linear-gradient(150deg,#43464D 0%,#0C0E12 56%)",
          borderRadius: 44,
          padding: 5,
          boxShadow:
            "0 78px 104px -34px rgba(11,15,25,0.6), 0 34px 60px -24px rgba(11,15,25,0.4), inset 0 2px 1.5px rgba(255,255,255,0.18), inset 0 0 0 1.5px rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            position: "relative",
            background: "#0E0F11",
            borderRadius: 38,
            overflow: "hidden",
            height: 510,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Dynamic Island */}
          <div
            style={{
              position: "absolute",
              top: 11,
              left: "calc(50% - 24px)",
              width: 48,
              height: 21,
              background: "#000",
              borderRadius: 12,
              zIndex: 8,
            }}
          />

          {/* iOS status bar */}
          <div
            style={{
              flexShrink: 0,
              height: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 13px",
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: "-0.02em",
                fontVariantNumeric: TABULAR,
                fontFamily: H.sans,
              }}
            >
              {t("k941")}
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <svg
                width="17"
                height="12"
                viewBox="0 0 17 12"
                fill="#fff"
                style={{ display: "block" }}
              >
                <rect x="0" y="7.5" width="3" height="4.5" rx="1" />
                <rect x="4.67" y="5" width="3" height="7" rx="1" />
                <rect x="9.33" y="2.5" width="3" height="9.5" rx="1" />
                <rect x="14" y="0" width="3" height="12" rx="1" />
              </svg>
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                stroke="#fff"
                strokeWidth="1.55"
                strokeLinecap="round"
                style={{ display: "block" }}
              >
                <path d="M1.72 5.23 A8.2 8.2 0 0 1 14.28 5.23" />
                <path d="M4.63 7.67 A4.4 4.4 0 0 1 11.37 7.67" />
                <circle cx="8" cy="10" r="1" fill="#fff" stroke="none" />
              </svg>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <span
                  style={{
                    position: "relative",
                    width: 23,
                    height: 11.5,
                    border: "1px solid rgba(255,255,255,0.5)",
                    borderRadius: 3,
                    boxSizing: "border-box",
                    padding: 1.4,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      background: "#fff",
                      borderRadius: 1.5,
                    }}
                  />
                </span>
                <span
                  style={{
                    width: 1.5,
                    height: 4,
                    background: "rgba(255,255,255,0.5)",
                    borderRadius: "0 1.5px 1.5px 0",
                  }}
                />
              </span>
            </span>
          </div>

          {/* Header row */}
          <div
            style={{
              flexShrink: 0,
              padding: "2px 14px 11px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                color: "rgba(255,255,255,0.85)",
                display: "inline-flex",
                marginLeft: -2,
              }}
            >
              <ChevronLeft width={22} height={22} strokeWidth={2.2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontFamily: H.sans,
                }}
              >
                {t("wegLindenhof")}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: "rgba(255,255,255,0.45)",
                  fontFamily: H.sans,
                }}
              >
                {t("k4Teilnehmer1204")}
              </div>
            </div>
            <span
              style={{ color: "rgba(255,255,255,0.6)", display: "inline-flex" }}
            >
              <Users width={18} height={18} strokeWidth={2} />
            </span>
          </div>

          {/* Main video — Sandra Holtkamp */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              margin: "0 12px",
              borderRadius: 18,
              overflow: "hidden",
              background: "#202327",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                background: "#39414B",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 34,
                fontWeight: 600,
                fontFamily: H.sans,
              }}
            >
              {t("sh")}
            </span>
            <div
              style={{
                position: "absolute",
                left: 10,
                bottom: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(5px)",
                WebkitBackdropFilter: "blur(5px)",
                padding: "3px 9px",
                borderRadius: 8,
              }}
            >
              <MicOff
                width={12}
                height={12}
                strokeWidth={2}
                style={{ color: "#F26D6D" }}
              />
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#fff",
                  fontFamily: H.sans,
                }}
              >
                {t("sandraHoltkamp")}
              </span>
            </div>
          </div>

          {/* Phone control row */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 11,
              padding: "14px 0 9px",
            }}
          >
            <span
              style={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mic width={19} height={19} strokeWidth={2} />
            </span>
            <span
              style={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: "50%",
                background: "#D8453E",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoOff width={19} height={19} strokeWidth={2} />
            </span>
            <span
              style={{
                flexShrink: 0,
                height: 42,
                padding: "0 17px",
                borderRadius: 21,
                background: "#D8453E",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: H.sans,
              }}
            >
              <PhoneOff width={16} height={16} strokeWidth={2} />
              {t("verlassen")}
            </span>
          </div>

          {/* Home indicator */}
          <div
            style={{
              flexShrink: 0,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                width: 115,
                height: 5,
                borderRadius: 3,
                background: "rgba(255,255,255,0.85)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** "Online-Meetings" module — chromeless full-bleed dark video grid. */
export function ModuleMeeting(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleMeeting");
  return (
    <div
      style={{
        position: "relative",
        height: MODULE_HEIGHT,
        background: "#0E0F11",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        containerType: "inline-size",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          flexShrink: 0,
          height: 50,
          display: "flex",
          alignItems: "center",
          padding: "0 18px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#33C27F",
              boxShadow: "0 0 0 3px rgba(51,194,127,0.18)",
            }}
          />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              fontFamily: H.sans,
            }}
          >
            {t("videoMeeting")}
          </span>
          <span
            style={{
              fontSize: 12.5,
              color: "rgba(255,255,255,0.4)",
              fontFamily: H.sans,
            }}
          >
            {t("nbspWegLindenhof")}
          </span>
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <span
            style={{
              fontFamily: H.label,
              fontSize: 13,
              color: "rgba(255,255,255,0.5)",
              fontVariantNumeric: TABULAR,
            }}
          >
            {t("k1204")}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 13,
              color: "rgba(255,255,255,0.72)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 8,
              padding: "6px 12px",
              cursor: "pointer",
              fontFamily: H.sans,
            }}
          >
            <LinkIcon width={14} height={14} strokeWidth={1.9} />
            {t("linkKopieren")}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 13,
              fontWeight: 600,
              color: H.ink,
              background: "#fff",
              borderRadius: 8,
              padding: "6px 13px",
              cursor: "pointer",
              fontFamily: H.sans,
            }}
          >
            <UserPlus width={14} height={14} strokeWidth={2} />
            {t("einladen")}
          </span>
        </div>
      </div>

      <VideoGrid />
      <ControlDock />
    </div>
  );
}
